import { Body, Button, IconButton } from '@assembly-js/design-system';
import { Skeleton } from '@mui/material';
import { t } from 'i18next';
import React from 'react';

import { formatDollars } from 'src/routes/settings/plans/components/studio/studioPlanFeatures';
import type { ExtraAppPreviewResponse } from 'src/services/api/studioCreditApi';

import type { AppLimitSurface } from './appLimit';
import { type AppLimitApp, AppLimitAppCard } from './AppLimitAppCard';
import { AppLimitModalShell } from './AppLimitModalShell';

interface AppLimitModalProps {
  onClose: () => void;
  /** Takes the route the modal offered — pays the per-app fee. */
  onConfirm: () => void;
  /**
   * Stripe's quote for one more app: the recurring per-app amount and whatever
   * is prorated for the rest of this period. Undefined while it is in flight.
   */
  feePreview?: ExtraAppPreviewResponse;
  /**
   * Whether the quote came back unusable. Confirming still charges the correct
   * amount — the server prices it, not this quote — so an unquotable workspace
   * is let through with the price left unsaid rather than held behind a CTA that
   * can never enable.
   */
  isFeeUnquoted?: boolean;
  /**
   * Re-requests the quote. Given when the caller can retry: the price is worth
   * one more attempt before the user commits without seeing it.
   */
  onRetryFee?: () => void;
  /**
   * Whether the per-app charge is in flight. The modal stays open and shows the
   * CTA loading — the charge has to settle before the work it pays for starts,
   * so closing early would hide the only progress the user can see.
   */
  isConfirming?: boolean;
  /**
   * Whether the charge was refused. Keeps the user here with a reason and the
   * CTA live to retry, rather than closing on a failure.
   */
  hasChargeFailed?: boolean;
  /** The action being held back. */
  surface: AppLimitSurface;
  /** The app the paywall interrupted, shown as its own card. */
  app?: AppLimitApp;
}

const MODAL_WIDTH_PX = 344;

/** Stripe's recurring per-app amount, e.g. "$8/month". */
const formatFee = (preview: ExtraAppPreviewResponse): string =>
  t(($) => $.page_apps.app_builder.app_limit_modal.fee, {
    amount: formatDollars(preview.recurringAmount),
    interval:
      preview.interval === 'annual'
        ? t(($) => $.page_apps.app_builder.app_limit_modal.interval.year)
        : t(($) => $.page_apps.app_builder.app_limit_modal.interval.month),
  });

/** The line under the title: the fee, where a quote exists to price it. */
const getDescription = (
  surface: AppLimitSurface,
  fee: string | undefined,
): string => {
  if (!fee) {
    return surface === 'publish'
      ? t(
          ($) =>
            $.page_apps.app_builder.app_limit_modal.description
              .publish_unpriced,
        )
      : t(
          ($) =>
            $.page_apps.app_builder.app_limit_modal.description.add_unpriced,
        );
  }
  return surface === 'publish'
    ? t(($) => $.page_apps.app_builder.app_limit_modal.description.publish, {
        fee,
      })
    : t(($) => $.page_apps.app_builder.app_limit_modal.description.add, {
        fee,
      });
};

const getCta = (surface: AppLimitSurface): string =>
  surface === 'publish'
    ? t(($) => $.page_apps.app_builder.app_limit_modal.cta.publish)
    : t(($) => $.page_apps.app_builder.app_limit_modal.cta.add);

/**
 * The paid-plan paywall: adding another app is allowed, it just costs the
 * per-app fee. Shared by both surfaces that can reach the limit — publishing an
 * app the workspace built and adding a marketplace app.
 *
 * Free never lands here. It is the one plan the backend blocks from extra apps
 * outright, and it also has a smaller allowance than the paid plans, so its way
 * past the limit is an upgrade — see {@link AppLimitUpgradeModal}.
 */
export const AppLimitModal: React.FC<AppLimitModalProps> = ({
  onClose,
  onConfirm,
  feePreview,
  isFeeUnquoted = false,
  onRetryFee,
  isConfirming = false,
  hasChargeFailed = false,
  surface,
  app,
}) => {
  const title = t(($) => $.page_apps.app_builder.app_limit_modal.title);

  // RTK Query keeps the last good quote in `feePreview` when a later refresh
  // errors, so a stale price can arrive alongside isFeeUnquoted. Once the quote
  // is unusable, drop it so the modal never shows a price Stripe has moved off of.
  const quote = isFeeUnquoted ? undefined : feePreview;
  // Drives the skeleton: a quote is on its way and has neither landed nor failed.
  const isQuotePending = !quote && !isFeeUnquoted;
  // The CTA commits to a real charge, so it enables only with a quote on screen
  // — never on a pending or errored one, so the user never confirms a price they
  // were not shown. An unusable quote is not a dead end: the fee_error row
  // offers a Retry that re-requests it.
  const isReady = quote !== undefined;
  // The close affordance goes too: the charge is in flight and there is nothing
  // useful to do but wait for it.
  const isLocked = isConfirming;

  return (
    <AppLimitModalShell
      ariaLabel={title}
      widthPx={MODAL_WIDTH_PX}
      isDismissDisabled={isLocked}
      onClose={onClose}
    >
      {/* The phone frame tightens the padding and the gap under the heading a
          step, and nudges the heading 2px down to sit level with its taller
          close button. Everything else measures the same at both widths. */}
      <div className="flex flex-col gap-4 bg-white p-4 pt-3 sm:gap-5 sm:p-5">
        <div className="flex items-start gap-3">
          <div className="flex min-w-0 flex-1 flex-col gap-1 pt-0.5 sm:pt-0">
            <Body
              tag="div"
              size="lg"
              className="font-medium text-black-heading"
            >
              {title}
            </Body>
            {/* The price is Stripe's rather than a constant: the catalog owns the
                per-app amount and the interval follows the subscription. */}
            {isQuotePending ? (
              <Skeleton variant="text" width="80%" />
            ) : (
              <Body tag="div" className="leading-[22px] text-gray-small">
                {getDescription(surface, quote ? formatFee(quote) : undefined)}
              </Body>
            )}
          </div>
          <IconButton
            icon="Close"
            variant="minimal"
            size="sm"
            aria-label={t(($) => $.page_apps.app_builder.app_limit_modal.close)}
            onClick={onClose}
            disabled={isLocked}
            // A larger tap target on a phone, per the mobile frame.
            className="size-7 shrink-0 rounded hover:bg-gray-150 sm:size-6"
          />
        </div>

        {/* Held together in their own group so the 20px between the card and the
            CTA survives the outer gap tightening on a phone. */}
        <div className="flex flex-col gap-5">
          {app && <AppLimitAppCard app={app} />}

          <div className="flex flex-col gap-2">
            <Button
              variant="primary"
              size="sm"
              label={getCta(surface)}
              className="box-border w-full"
              loading={isLocked}
              disabled={!isReady || isLocked}
              onClick={onConfirm}
            />
            {/* A refused charge means nothing was started, so the CTA stays live
                and this says why the user is still here. */}
            {hasChargeFailed && !isConfirming && (
              <Body tag="div" size="sm" className="text-center text-red-600">
                {t(
                  ($) => $.page_apps.app_builder.app_limit_modal.charge_failed,
                )}
              </Body>
            )}
            {/* Says why no price is shown. Keyed off isFeeUnquoted alone, not the
                raw feePreview: RTK Query can retain a stale quote through a failed
                refresh, and gating on its absence would hide this error — and its
                retry — exactly when the price on screen is out of date. The CTA
                stays live — the charge is raised on confirm either way — but the
                user gets one attempt at seeing the number first. */}
            {isFeeUnquoted && !hasChargeFailed && (
              <div className="flex items-center justify-center gap-1">
                <Body tag="div" size="sm" className="text-gray-small">
                  {t(($) => $.page_apps.app_builder.app_limit_modal.fee_error)}
                </Body>
                {onRetryFee && (
                  <Button
                    variant="text"
                    size="sm"
                    label={t(
                      ($) => $.page_apps.app_builder.app_limit_modal.fee_retry,
                    )}
                    onClick={onRetryFee}
                  />
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </AppLimitModalShell>
  );
};
